import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";

const LADDER = [
  ["1. Tool", "Software", "\"An AI chatbot\"", "$50 to $300/mo", "Other software"],
  ["2. Service", "Your labor", "\"I'll set up AI for you\"", "$500 to $1,500", "Other freelancers"],
  ["3. System", "A process", "\"An automated intake process\"", "$2,000 to $4,000", "Agencies"],
  ["4. Outcome", "A business result", "\"Never lose another after-hours lead\"", "$5,000+", "A salary or a lost deal"],
];

const NAMING_EXAMPLES = [
  "24/7 Lead Conversion System",
  "60-Second Speed-to-Lead System",
  "Dead Database Revenue Recovery System",
  "After-Hours Case Capture System",
  "Unscheduled Treatment Recovery System",
];

const ANCHORS = [
  ["The Payroll Anchor:", "\"The role this replaces costs $38,000 to $52,000 a year, plus turnover, training, sick days, and the 16 hours a day they don't work.\""],
  ["The Leak Anchor:", "\"You told me you get roughly 40 leads a month and miss about a third. At your average job value, that's $X walking out the door every month.\" (Use THEIR numbers from the call. The math lands hardest when they supply the inputs.)"],
  ["The Competitor Anchor:", "\"Your competitors answer in 4 hours. This answers in 40 seconds. Speed-to-lead studies across industries consistently show response within minutes multiplies contact and conversion rates. Whoever answers first usually wins the deal.\""],
];

const GRAND_SLAM = [
  ["Core System:", "the AI delivery system itself, built to their business."],
  ["Speed Bonus:", "live in 14 days or faster (people pay premiums for speed)."],
  ["Effort Remover:", "\"we handle 100% of the setup, you approve two things and you're done.\""],
  ["Risk Reversal:", "a performance-based condition. Example: \"If it doesn't book at least X qualified appointments in the first 30 days, we work free until it does.\""],
  ["Scarcity (real only):", "limited onboarding slots per month because setup requires your attention. Never fake this."],
];

const money = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

function OfferDoc() {
  const [d, setD] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/offer-doc").then((r) => setD(r.data || {})).catch(() => {}); }, []);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const leak = useMemo(() => {
    const l = Number(d.leakLeads) || 0, p = Number(d.leakPct) || 0, v = Number(d.leakValue) || 0;
    return l * (p / 100) * v;
  }, [d.leakLeads, d.leakPct, d.leakValue]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/offer-doc", { data: { ...d, monthlyLeak: leak } }); toast.success("Offer doc saved to your account"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  // Called as a function (not a <Component/>) so the <input> stays in OfferDoc's stable
  // element tree and never remounts on keystroke. Text input + numeric inputmode.
  const field = (k, placeholder, w, numeric) => (
    <input data-testid={`offer-${k}`} type="text" inputMode={numeric ? "numeric" : undefined}
      value={d[k] || ""}
      onChange={(e) => set(k, numeric ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value)}
      placeholder={placeholder}
      style={{ border: "none", borderBottom: "1.5px solid var(--line)", padding: "4px 6px", fontFamily: "IBM Plex Mono, monospace", fontSize: 14, minWidth: w || 120, background: "transparent", flex: w ? "none" : 1 }} />
  );

  const line = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, margin: "10px 0", fontFamily: "IBM Plex Mono, monospace", fontSize: 14 };

  return (
    <div data-testid="offer-doc-form" style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 22, background: "#fff", marginTop: 12 }}>
      <div style={line}><strong>OFFER NAME:</strong> {field("offerName", "_______________")} <span>System</span></div>
      <div style={line}><strong>MARKET:</strong> {field("market", "_______________")}</div>

      <div style={{ margin: "14px 0" }}>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}><strong>THE OUTCOME</strong> (one sentence, no tech words):</div>
        {field("outcome", '"Never ____ again" or "Every ____, automatically."')}
      </div>

      <div style={{ margin: "14px 0" }}>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}><strong>WHAT IT REPLACES</strong> (the payroll anchor):</div>
        <div style={line}>Role: {field("replacesRole", "________", 160)} Typical cost: ${field("replacesCost", "____", 90, true)}/yr</div>
      </div>

      <div style={{ margin: "14px 0" }}>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}><strong>THE LEAK IT PLUGS</strong> (the math):</div>
        <div style={line}>
          {field("leakLeads", "___", 70, true)} leads/cases/renewals per month ×
          {field("leakPct", "__", 50, true)}% currently lost × ${field("leakValue", "____", 80, true)} avg value
        </div>
        <div style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: 18, color: "#2038E6", marginTop: 4 }} data-testid="offer-leak">= ${money(leak)}/mo currently leaking</div>
      </div>

      <div style={{ margin: "14px 0" }}>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}><strong>HOW IT WORKS</strong> (3 steps, customer-facing, still no tech words):</div>
        <div style={line}>1. Every inquiry gets answered in under {field("howSeconds", "__", 50, true)} seconds</div>
        <div style={{ ...line, margin: "4px 0" }}>2. The system qualifies using your exact criteria</div>
        <div style={{ ...line, margin: "4px 0" }}>3. Qualified prospects land on your calendar automatically</div>
      </div>

      <div style={{ margin: "14px 0" }}>
        <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}><strong>WHAT THE BUILD ACTUALLY REQUIRES</strong> (for your freelancer only):</div>
        <div style={line}>Channels: {field("buildChannels", "web chat / SMS / voice / DM")}</div>
        <div style={line}>Connects to: {field("buildConnects", "their calendar, their CRM")}</div>
        <div style={line}>Qualification logic: {field("buildQual", "the 4 to 6 questions that define a good lead")}</div>
        <div style={line}>Handoff rule: {field("buildHandoff", "when a human takes over")}</div>
      </div>

      <div style={line}><strong>PRICE:</strong> ${field("priceSetup", "____", 90, true)} setup + ${field("priceMonthly", "____", 90, true)}/mo</div>
      <div style={line}><strong>GUARANTEE:</strong> If it doesn't {field("guaranteeIf", "____", 140)} in 30 days, {field("guaranteeThen", "____", 140)}.</div>
      <div style={line}><strong>ONBOARDING CAPACITY:</strong> {field("onboardingCapacity", "__", 60, true)} new clients per month (real number).</div>

      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        <button className="btn-cobalt" onClick={save} disabled={saving} data-testid="offer-doc-save"><Save size={16} /> {saving ? "Saving..." : "Save to account"}</button>
        <button className="btn-outline" onClick={() => window.print()} data-testid="offer-doc-export"><Download size={16} /> Export PDF</button>
      </div>
    </div>
  );
}

export default function PositioningFrameworks() {
  return (
    <div className="prose-weekend" data-testid="asset-body-positioning">
      <p style={{ color: "var(--muted)" }}>Turn Cheap Tools Into Outcome-Priced Packages</p>
      <p style={{ fontSize: 15 }}>How to use this: Saturday afternoon. Work through the four frameworks in order, then complete the One-Page Offer Doc. That single page becomes your sales asset AND your build spec.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>FRAMEWORK 1: The Positioning Ladder</h3>
      <p style={{ fontSize: 15 }}>Every AI offer sits on one of four rungs. Each rung up roughly doubles the price ceiling. Same underlying technology on every rung.</p>
      <table>
        <thead><tr><th>Rung</th><th>You're Selling</th><th>Sounds Like</th><th>Price Ceiling</th><th>Compared Against</th></tr></thead>
        <tbody>{LADDER.map((r, i) => (<tr key={i}>{r.map((c, j) => <td key={j} style={{ fontSize: 14 }}>{c}</td>)}</tr>))}</tbody>
      </table>
      <p style={{ fontSize: 15 }}><strong>The rule:</strong> Never describe your offer below Rung 4. The moment you say the name of a tool or platform, you've priced yourself at Rung 1. Prospects compare you against whatever category you place yourself in. Place yourself against the payroll line, not the software line.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>FRAMEWORK 2: The Naming Formula</h3>
      <p style={{ fontSize: 15 }}>Outcome names follow this pattern:</p>
      <blockquote style={{ borderLeft: "4px solid #2038E6", margin: "12px 0", padding: "8px 16px", background: "#f2f3f0", fontFamily: "IBM Plex Mono, monospace", fontSize: 14 }}>[Timeframe or Always-On Quality] + [Business Result] + "System"</blockquote>
      <p style={{ fontSize: 15 }}>Examples built from the formula:</p>
      <ul>{NAMING_EXAMPLES.map((e, i) => <li key={i} style={{ margin: "4px 0" }}>{e}</li>)}</ul>
      <p style={{ fontSize: 15 }}>Test your name: if a stranger can tell what business result it produces without asking a single question, it passes. If they ask "what does it do?", it fails. "System" beats "bot," "agent," or "AI" in the name because systems sound owned and permanent; bots sound like subscriptions.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>FRAMEWORK 3: The Anchor Stack</h3>
      <p style={{ fontSize: 15 }}>Premium pricing is never justified by features. It's justified by comparison. Build three anchors and use them in this order on calls:</p>
      <ol style={{ paddingLeft: 20 }}>{ANCHORS.map(([b, t], i) => <li key={i} style={{ margin: "8px 0" }}><strong>{b}</strong> {t}</li>)}</ol>
      <p style={{ fontSize: 15 }}>Price presented AFTER anchors always sounds smaller than price presented before them. Never lead with price.</p>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 22 }}>FRAMEWORK 4: The Grand Slam Assembly</h3>
      <p style={{ fontSize: 15 }}>A premium offer is the core system plus everything that removes doubt, risk, and effort. Assemble yours from these five slots:</p>
      <ol style={{ paddingLeft: 20 }}>{GRAND_SLAM.map(([b, t], i) => <li key={i} style={{ margin: "8px 0" }}><strong>{b}</strong> {t}</li>)}</ol>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 26 }}>The One-Page Offer Doc (Complete This Now)</h3>
      <p style={{ fontSize: 15 }}>This page does triple duty: your positioning statement, your call framework, and the exact spec you hand your freelancer in Asset 05. Fill it in, save it to your account, and export it as a PDF.</p>
      <OfferDoc />
      <p style={{ fontSize: 15, marginTop: 12 }}>Print it. This page IS your business.</p>
    </div>
  );
}
